import { useEffect, useState, useContext, useRef, useCallback } from "react";
import { BoxcastChannel } from "../models/boxcastChannel";
import { getStreamingEpisodes } from "../service/StreamingService";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { StreamVideoContainer, StreamListContainer, StreamItem } from "./styles";
import StreamRow from "./StreamRow";
import { styled } from '@mui/material/styles';
import { UserContextType, useAuth } from "../auth";
import ModernChat from "../chat/ModernChat";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import Fab from "@mui/material/Fab";
import ChatIcon from '@mui/icons-material/Chat';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Grid from "@mui/material/Grid"; // Keep Grid for StreamsGrid
import StreamingTour from "./StreamingTour";
import { ChatProvider, useChat } from "../chat/ChatContext";
import Badge from "@mui/material/Badge";

const PageContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(4, 2),
  minHeight: '100vh',
  position: 'relative',
  background: 'linear-gradient(to bottom, #0f0f1e 0%, #1a1a2e 50%, #16213e 100%)',
}));

const VideoWrapper = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  width: '100%',
  maxWidth: '1200px', // constrain width slightly for easier viewing
  margin: '0 auto'
}));

const StreamsGrid = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(2),
  gap: theme.spacing(2),
}));

export interface StreamingProps {
  channelId?: string;
}

const ACCOUNT_CHANNEL_ID = "luczxkb5pyiet1wvuwhe";

const StreamingContent: React.FC<StreamingProps> = (props) => {
  const [streams, setStreams] = useState<BoxcastChannel[]>();
  const { IsAuthenticated } = useContext<UserContextType>(useAuth);
  // Replaced local chatOpen state with context
  const { unreadCount, isOpen, setIsOpen, hasNewUser } = useChat();

  const boxcastContextRef = useRef<any>(null);
  const firstPlayRef = useRef(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const loadBroadcast = useCallback((broadcastId: string) => {
    if (!boxcastContextRef.current) return;

    const options = {
      autoplay: true,
      showTitle: false,
      showDescription: false,
      showHighlights: true,
      showRelated: false,
      showCountdown: true,
      selectedBroadcastId: broadcastId,
      disableRedirectRulesOnLoad: true, // Prevents the SDK from incorrectly redirecting based on URL path
      market: "house-of-worship",
      defaultVideo: "next",
      showDocuments: true,
      showIndex: true,
    };

    boxcastContextRef.current.unload();
    boxcastContextRef.current.loadChannel(ACCOUNT_CHANNEL_ID, options);

    if (window.gtag) {
      window.gtag('event', 'watch_stream', {
        event_category: 'engagement',
        event_label: broadcastId,
        broadcast_id: broadcastId
      });
    }
  }, []);

  const changeSelectedBroadcast = useCallback((channel: BoxcastChannel) => {
    loadBroadcast(channel.id);
    if (firstPlayRef.current) {
      setTimeout(() => {
        loadBroadcast(channel.id);
      }, 3000);
      firstPlayRef.current = false;
    }
  }, [loadBroadcast]);

  // Fetch stream data first
  useEffect(() => {
    const fetchData = async () => {
      setStreams(await getStreamingEpisodes());
    };
    fetchData();
  }, []);

  // Initialize BoxCast widget only once
  useEffect(() => {
    if (typeof window !== 'undefined' && window.boxcast && !boxcastContextRef.current) {
      boxcastContextRef.current = window.boxcast(`#boxcast-widget-${ACCOUNT_CHANNEL_ID}`);
    }
  }, []);

  // Load initial broadcast once streams are loaded
  useEffect(() => {
    if (boxcastContextRef.current && streams && streams.length > 0) {
      const firstBroadcastId = streams[0].id;
      loadBroadcast(firstBroadcastId);
    }

    return () => {
      // Cleanup on unmount
      if (boxcastContextRef.current) {
        boxcastContextRef.current.unload();
      }
    };
  }, [streams, loadBroadcast]);

  const getStream = () => {
    return streams
      ?.slice(1, streams.length)
      .map((c) => (
        <Grid item xs={12} sm={6} md={4} key={c.id}>
          <StreamItem>
            <StreamRow onPlay={changeSelectedBroadcast} channel={c} />
          </StreamItem>
        </Grid>
      ));
  };

  return (
    <PageContainer maxWidth="xl">
      <StreamingTour />
      {/* Main Video Area */}
      <VideoWrapper>
        <StreamVideoContainer>
          <div
            id={`boxcast-widget-${ACCOUNT_CHANNEL_ID}`}
            data-channel={ACCOUNT_CHANNEL_ID}
          ></div>
        </StreamVideoContainer>
      </VideoWrapper>

      {/* Chat Toggle Button */}
      <Fab
        id="chat-fab"
        color="primary"
        aria-label="chat"
        onClick={() => setIsOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 30,
          right: 30,
          zIndex: 1000,
          width: 60,
          height: 60
        }}
      >
        <Badge badgeContent={unreadCount} color="error" invisible={unreadCount === 0 && !hasNewUser}>
          <ChatIcon />
        </Badge>
      </Fab>

      {/* Slide-out Chat Drawer */}
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : '400px',
            background: 'transparent',
            boxShadow: 'none'
          }
        }}
        ModalProps={{
          keepMounted: true // Better performance on mobile
        }}
      >
        <Box height="100%" display="flex" flexDirection="column">
          {IsAuthenticated && IsAuthenticated() ? (
            <ModernChat onClose={() => setIsOpen(false)} />
          ) : (
            <Box
              height="100%"
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              p={3}
              sx={{
                bgcolor: "rgba(20, 20, 35, 0.95)",
                backdropFilter: "blur(20px)",
                color: "white"
              }}
            >
              <Typography variant="h6" gutterBottom>Join the conversation</Typography>
              <Button variant="contained" color="secondary" href="/login">Sign in to Chat</Button>
              <Button sx={{ marginTop: 2, color: 'rgba(255,255,255,0.5)' }} onClick={() => setIsOpen(false)}>Close</Button>
            </Box>
          )}
        </Box>
      </Drawer>

      <StreamListContainer>
        <Typography
          variant="h4"
          component="h2"
          gutterBottom
          sx={{
            fontWeight: 700,
            color: 'white',
            marginBottom: 3,
            fontSize: { xs: '1.75rem', sm: '2.125rem' },
            background: 'linear-gradient(135deg, #ffffff 0%, #a78bfa 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Available Streams
        </Typography>
        <StreamsGrid container spacing={3}>
          {streams && getStream()}
        </StreamsGrid>
      </StreamListContainer>
    </PageContainer>
  );
};

const Streaming: React.FC<StreamingProps> = (props) => {
  return (
    <ChatProvider>
      <StreamingContent {...props} />
    </ChatProvider>
  );
};

export default Streaming;
